import React, { useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import {
  Box,
  Typography,
  Button,
  TextField,
  Modal,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  DialogTitle,
  DialogActions,
  Snackbar,
  Alert,
  Stack,
  Chip,
  Divider,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AddCommentIcon from "@mui/icons-material/AddComment";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import api, { API_BASE_URL } from "../../../src/utils/api";

const ViolationComments = ({ violation_id, currentUserId, event_id = null }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [commentType, setCommentType] = useState("internal");
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [imageURL, setImageURL] = useState(null);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmExternalOpen, setConfirmExternalOpen] = useState(false);
  const [pendingType, setPendingType] = useState("external");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseSnackbar = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.98)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  };

  useEffect(() => {
    if (!violation_id) return;

    const fetchComments = async () => {
      setIsLoading(true);
      try {
        const response = await api.get(`/getViolationComments/${violation_id}`);
        setComments(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Failed to fetch comments:", error);
        setComments([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchComments();
  }, [violation_id]);

  const onDrop = useCallback((acceptedFiles) => {
    setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
  });

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCommentTypeChange = (e) => {
    const selectedType = e.target.value;
    if (selectedType === "external") {
      setPendingType(selectedType);
      setConfirmExternalOpen(true);
    } else {
      setCommentType(selectedType);
    }
  };

  const confirmExternalYes = () => {
    setCommentType(pendingType);
    setConfirmExternalOpen(false);
  };

  const confirmExternalCancel = () => {
    setCommentType("internal");
    setConfirmExternalOpen(false);
  };

  const handleEditCommentClick = (comment) => {
    setEditingComment(comment);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingComment || !editingComment.comment_text?.trim()) return;

    try {
      await api.put(`/editViolationComment/${editingComment.comment_id}`, {
        comment_text: editingComment.comment_text,
        edited_by: currentUserId,
      });

      const response = await api.get(`/getViolationComments/${violation_id}`);
      setComments(Array.isArray(response.data) ? response.data : []);

      setIsEditModalOpen(false);
      setSnackbar({
        open: true,
        message: "✅ Comment updated successfully.",
        severity: "success",
      });
    } catch (err) {
      console.error("Error editing comment:", err);
      setSnackbar({
        open: true,
        message: "❌ Failed to save comment update.",
        severity: "error",
      });
    }
  };

  const handleViewHistoryClick = async (comment) => {
    try {
      const response = await api.get(
        `/violation-comment-history/${comment.comment_id}`
      );
      setHistoryRecords(Array.isArray(response.data) ? response.data : []);
      setIsHistoryModalOpen(true);
    } catch (err) {
      console.error("Failed to fetch comment history:", err);
      setSnackbar({
        open: true,
        message: "❌ Could not load comment history.",
        severity: "error",
      });
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      setSnackbar({
        open: true,
        message: "Comment cannot be empty.",
        severity: "warning",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const commentPayload = {
        violation_id,
        user_id: currentUserId,
        comment_text: newComment.trim(),
        is_internal: commentType === "internal" ? 1 : 0,
        event_id: event_id || "",
      };

      const response = await api.post(`/addViolationComment`, commentPayload);
      const newCommentId = response.data?.id;

      if (!newCommentId) {
        setSnackbar({
          open: true,
          message: "❌ Failed to create comment.",
          severity: "error",
        });
        return;
      }

      if (selectedFiles.length > 0) {
        const formData = new FormData();
        formData.append("request_id", violation_id);
        formData.append("comment_id", newCommentId);
        formData.append("uploaded_by", currentUserId);

        selectedFiles.forEach((file) => formData.append("upload_urls", file));

        await api.post("/violation-files", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      const refreshed = await api.get(`/getViolationComments/${violation_id}`);
      setComments(Array.isArray(refreshed.data) ? refreshed.data : []);

      setNewComment("");
      setCommentType("internal");
      setSelectedFiles([]);
      setIsModalOpen(false);

      setSnackbar({
        open: true,
        message: "✅ Comment and files added successfully.",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to add comment or upload files:", error);
      setSnackbar({
        open: true,
        message: "❌ Failed to add comment. Please try again.",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 3 },
            pt: 2.25,
            pb: 1.5,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <ChatBubbleOutlineIcon sx={{ color: "#2f63ad" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Comments
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Review internal and external comments, attachments, and edit history.
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2.5,
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddCommentIcon />}
              onClick={() => setIsModalOpen(true)}
              sx={primaryButtonSx}
            >
              Add Comment
            </Button>
          </Box>

          {isLoading ? (
            <Box
              sx={{
                minHeight: "180px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : comments.length > 0 ? (
            <Stack spacing={2}>
              {comments.map((comment, index) => {
                const isInternal = !!comment.is_internal;

                return (
                  <Card
                    key={comment.comment_id ?? index}
                    sx={{
                      borderRadius: "16px",
                      border: "1px solid rgba(47,99,173,0.08)",
                      boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                      backgroundColor: "#fff",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: "1px solid rgba(47,99,173,0.08)",
                        backgroundColor: isInternal
                          ? "rgba(220,38,38,0.05)"
                          : "rgba(47,99,173,0.05)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip
                        label={isInternal ? "Internal Comment" : "External Comment"}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          borderRadius: "999px",
                          color: isInternal ? "#b91c1c" : "#1d4ed8",
                          backgroundColor: isInternal
                            ? "rgba(220,38,38,0.12)"
                            : "rgba(59,130,246,0.12)",
                        }}
                      />

                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditCommentClick(comment)}
                          title="Edit Comment"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>

                        {comment.edited_at && (
                          <IconButton
                            size="small"
                            onClick={() => handleViewHistoryClick(comment)}
                            title="View History"
                          >
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ p: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#223142",
                          whiteSpace: "pre-wrap",
                          mb: 1.25,
                        }}
                      >
                        {comment.comment_text}
                      </Typography>

                      {comment.files && comment.files.length > 0 && (
                        <Box sx={{ mt: 1.25 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: "#223142", mb: 1 }}
                          >
                            Attachments
                          </Typography>

                          <Stack spacing={1}>
                            {comment.files.map((file, i) => {
                              const fileUrl = `${API_BASE_URL}${file.file_url}`;
                              const ext = (file.file_name || "")
                                .split(".")
                                .pop()
                                ?.toLowerCase();
                              const isImage = [
                                "jpg",
                                "jpeg",
                                "png",
                                "gif",
                                "bmp",
                                "webp",
                              ].includes(ext);

                              return (
                                <Box key={file.id ?? i}>
                                  {isImage ? (
                                    <Card
                                      elevation={0}
                                      sx={{
                                        p: 1,
                                        cursor: "pointer",
                                        maxWidth: 220,
                                        borderRadius: "12px",
                                        border: "1px solid rgba(47,99,173,0.08)",
                                        boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                                      }}
                                      onClick={() => {
                                        setImageURL(fileUrl);
                                        setOpenImageModal(true);
                                      }}
                                    >
                                      <img
                                        src={fileUrl}
                                        alt={file.file_name}
                                        style={{
                                          width: "100%",
                                          borderRadius: "8px",
                                          display: "block",
                                        }}
                                      />
                                    </Card>
                                  ) : (
                                    <Typography variant="body2">
                                      <a
                                        href={fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          color: "#1976d2",
                                          textDecoration: "none",
                                          fontWeight: 500,
                                        }}
                                      >
                                        {file.file_name} ⬇️
                                      </a>
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Stack>
                        </Box>
                      )}

                      <Divider
                        sx={{ my: 1.5, borderColor: "rgba(47,99,173,0.08)" }}
                      />

                      <Typography variant="caption" sx={{ color: "#6b778c" }}>
                        Added by {comment.user_name} {comment.user_lastname || ""} on{" "}
                        {comment.sent_at
                          ? new Date(comment.sent_at).toLocaleString()
                          : "N/A"}
                      </Typography>
                    </Box>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <Card sx={{ ...pageCardSx, p: 3, textAlign: "center" }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
                No comments available
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.75 }}>
                Add the first comment for this violation.
              </Typography>
            </Card>
          )}
        </Box>
      </Card>

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92%", sm: 520 },
            maxHeight: "90vh",
            overflowY: "auto",
            backgroundColor: "rgba(255,255,255,0.98)",
            padding: 0,
            borderRadius: "18px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
            border: "1px solid rgba(47,99,173,0.08)",
          }}
        >
          <Box
            sx={{
              px: 3,
              pt: 2.5,
              pb: 1.5,
              borderBottom: "1px solid rgba(47,99,173,0.08)",
              backgroundColor: "rgba(47,99,173,0.02)",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Add Comment
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
              Add an internal or external comment and optionally attach files.
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            <TextField
              fullWidth
              label="Comment"
              multiline
              rows={4}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              sx={{ ...textFieldSx, mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Comment Type</InputLabel>
              <Select
                value={commentType}
                onChange={handleCommentTypeChange}
                label="Comment Type"
                sx={textFieldSx}
              >
                <MenuItem value="external">External</MenuItem>
                <MenuItem value="internal">Internal</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 600, color: "#223142" }}
              >
                Attach Files
              </Typography>

              <Paper
                variant="outlined"
                {...getRootProps()}
                sx={{
                  p: 2.5,
                  border: "2px dashed #90caf9",
                  borderRadius: "14px",
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: isDragActive ? "#e3f2fd" : "#fafafa",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f5faff",
                    borderColor: "#1976d2",
                  },
                }}
              >
                <input {...getInputProps()} />
                <CloudUploadIcon sx={{ fontSize: 40, color: "#1976d2", mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {isDragActive
                    ? "Drop files here..."
                    : "Drag & drop files here, or click to select"}
                </Typography>
              </Paper>

              {selectedFiles.length > 0 && (
                <List
                  dense
                  sx={{
                    mt: 1.25,
                    maxHeight: 120,
                    overflowY: "auto",
                    border: "1px solid rgba(47,99,173,0.08)",
                    borderRadius: "12px",
                    backgroundColor: "rgba(47,99,173,0.02)",
                    px: 1,
                  }}
                >
                  {selectedFiles.map((file, idx) => (
                    <ListItem key={idx} sx={{ borderBottom: "1px solid #eee" }}>
                      <ListItemText
                        primary={file.name}
                        primaryTypographyProps={{
                          noWrap: true,
                          sx: { color: "#223142", fontWeight: 500 },
                        }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveFile(idx)}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.25 }}>
              <Button
                variant="outlined"
                onClick={() => setIsModalOpen(false)}
                sx={secondaryButtonSx}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={handleAddComment}
                disabled={isSubmitting}
                sx={primaryButtonSx}
                startIcon={
                  isSubmitting ? <CircularProgress size={18} color="inherit" /> : null
                }
              >
                {isSubmitting ? "Saving..." : "Save Comment"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Modal open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92%", sm: 500 },
            backgroundColor: "rgba(255,255,255,0.98)",
            borderRadius: "18px",
            border: "1px solid rgba(47,99,173,0.08)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 3,
              pt: 2.5,
              pb: 1.5,
              borderBottom: "1px solid rgba(47,99,173,0.08)",
              backgroundColor: "rgba(47,99,173,0.02)",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Edit Comment
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Comment Text"
              value={editingComment?.comment_text || ""}
              onChange={(e) =>
                setEditingComment((prev) => ({
                  ...(prev || {}),
                  comment_text: e.target.value,
                }))
              }
              sx={{ ...textFieldSx, mb: 2 }}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.25 }}>
              <Button
                variant="outlined"
                onClick={() => setIsEditModalOpen(false)}
                sx={secondaryButtonSx}
              >
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSaveEdit} sx={primaryButtonSx}>
                Save Changes
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Dialog
        open={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Comment History</DialogTitle>
        <DialogContent dividers>
          {historyRecords.length > 0 ? (
            historyRecords.map((record, index) => (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  p: 2,
                  border: "1px solid #ddd",
                  borderRadius: "12px",
                  backgroundColor: "#fafafa",
                }}
              >
                <Typography variant="subtitle2" sx={{ color: "#1976d2" }}>
                  ✏️ Edited By:{" "}
                  <strong>
                    {`${record.edited_by_name || "Unknown"} ${
                      record.edited_by_lastname || ""
                    }`}
                  </strong>
                </Typography>

                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>Edited On:</strong>{" "}
                  {record.edited_at
                    ? new Date(record.edited_at).toLocaleString()
                    : "N/A"}
                </Typography>

                <Box sx={{ mt: 1.5 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", color: "#b71c1c" }}
                  >
                    Old Comment:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-wrap",
                      backgroundColor: "#ffe6e6",
                      borderRadius: "8px",
                      p: 1,
                      mt: 0.5,
                    }}
                  >
                    {record.old_text || "(empty)"}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", mt: 1, color: "#1b5e20" }}
                  >
                    New Comment:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-wrap",
                      backgroundColor: "#e8f5e9",
                      borderRadius: "8px",
                      p: 1,
                      mt: 0.5,
                    }}
                  >
                    {record.new_text || "(empty)"}
                  </Typography>
                </Box>
              </Box>
            ))
          ) : (
            <Typography>No history found for this comment.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setIsHistoryModalOpen(false)} sx={secondaryButtonSx}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmExternalOpen}
        onClose={confirmExternalCancel}
        PaperProps={{ sx: dialogPaperSx }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 700,
            color: "#223142",
            pt: 3,
          }}
        >
          <WarningAmberIcon sx={{ color: "#ed6c02" }} />
          Send to Homeowner?
        </DialogTitle>
        <DialogContent>
          <Typography>
            This external comment will also be sent to the homeowner via email.
            Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.25 }}>
          <Button onClick={confirmExternalCancel} variant="outlined" sx={secondaryButtonSx}>
            Cancel
          </Button>
          <Button onClick={confirmExternalYes} variant="contained" sx={primaryButtonSx}>
            Yes, Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openImageModal}
        onClose={() => setOpenImageModal(false)}
        maxWidth="md"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogContent sx={{ position: "relative", textAlign: "center", p: 2 }}>
          <IconButton
            onClick={() => setOpenImageModal(false)}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={imageURL}
            alt="Full View"
            style={{ width: "100%", maxHeight: "90vh", objectFit: "contain" }}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          variant="filled"
          sx={{ borderRadius: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ViolationComments;